import Vue from 'vue'
import { ref } from '@vue/composition-api'
import { utils, constants as c, productUtil } from '@kingpin-global/kingpin-utils-frontend'
import {
  formatNumber, hasProductMaxQuantity, isProductValueDisabled, getMaxQuantity, imageLoadError,
} from '@/@core/utils/utils'
import { getStatusTextAndColorCode } from '@/common-utils'
import { getLineStatusText, isLineShipSelectable } from '@/composables/orderLineStatusText'
import constants, { PRODUCTS_LIMIT } from '@/constants'
import store from '@/store'
import { UPDATE_ORDER_PRODUCTS_DISCOUNT } from '@/store/modules/order.module'
import { apiToastSuccess, apiToastWarning, apiToastErrorV2 } from '@/@core/utils/toast'
import { useEcommerceUi } from '@/views/apps/useEcommerce'
import { parseProductItems } from '@/views/apps/checkout/cart-products-utils'
import { isLockedConfirmedLine, isOrderLineRemovable } from '@/composables/useOrderPartialConfirm'
import { filterOrderProductsBySearch } from '@/views/apps/order/order-product-search'

const { getProductAndOrderTotals } = productUtil
const { formatCurrency, formattedDate, convertValueToPercent } = utils
const { ORDER_STATUS, SHIPMENT_STATUS } = c
const { ORDER_PRODUCT_SHIPMENT_STATUS } = constants

export function useOrderProducts() {
  const variantLoaded = ref(false)
  const isLoaded = ref(false)
  const isSelectedProduct = ref(false)
  const products = ref([])
  const productsSlice = ref([])
  const productDiscounts = ref({})
  const isApplyingDiscount = ref(false)
  const editingDiscountProductIds = ref({})
  const originalDiscounts = ref({})
  const expandedPartialProducts = ref({})
  const colorHeading = 'color'
  const headings = ref([{
    key: colorHeading,
    label: 'Color',
  }])

  // KP-4363: products matching the search box; `products` keeps the full list
  // because shipment selection, ship and totals read it.
  const productSearchQuery = ref('')
  let visibleProducts = []

  let productsSliceX = 0
  let productsSliceY = 0
  let pageNumber = 1

  const { parseProductImage } = useEcommerceUi()

  // Scroll-based pagination
  function onScrollLoader() {
    if (window.scrollY > (document.body.scrollHeight - 2000) && visibleProducts.length > productsSliceX) {
      pageNumber += 1
      productsSliceY = visibleProducts.length < (pageNumber * PRODUCTS_LIMIT) ? visibleProducts.length : pageNumber * PRODUCTS_LIMIT
      visibleProducts.slice(productsSliceX, productsSliceY).forEach(p => productsSlice.value.push(p))
      productsSliceX = productsSliceY
    }
  }

  // Ensure non-carton products have product.quantity = 1
  function ensureCorrectQuantityStructure(order) {
    if (!order?.products) return
    order.products.forEach(product => {
      if (!product.isCarton) {
        product.quantity = 1
      }
    })
  }

  function updateTotalPriceAndUnits(order) {
    const role = store.getters.entityType
    getProductAndOrderTotals(role, order)
  }

  function onCartonQuantityChange(order) {
    updateTotalPriceAndUnits(order)
  }

  // Restarts the paginated list from the products matching the search.
  function resetProductsSlice() {
    productsSlice.value = []
    visibleProducts = filterOrderProductsBySearch(products.value, productSearchQuery.value) || []
    pageNumber = 1
    productsSliceY = visibleProducts.length < PRODUCTS_LIMIT ? visibleProducts.length : PRODUCTS_LIMIT
    visibleProducts.slice(0, productsSliceY).forEach(p => productsSlice.value.push(p))
    productsSliceX = productsSliceY
  }

  function setOrderProducts(orderProducts, order) {
    productsSlice.value = []
    if (orderProducts && orderProducts.length) {
      products.value = orderProducts
      resetProductsSlice()
      isLoaded.value = true
      ensureCorrectQuantityStructure(order)
    }
  }

  function searchOrderProducts(query) {
    productSearchQuery.value = query || ''
    resetProductsSlice()
  }

  // Product status helpers
  function isProductDeletedOrCancelled(product) {
    const isCancelled = product.status === ORDER_PRODUCT_SHIPMENT_STATUS.CANCELLED.LABEL
    return product.isDeleted || product.isDeletedByBrand || product.isDeletedByRetailer || isCancelled
  }

  function isProductDisabled(product, order, isShipment) {
    const isDeletedOrCancelled = isProductDeletedOrCancelled(product)
    if (order?.isDataForCreateShipment) {
      return isDeletedOrCancelled
    }
    return isShipment || isDeletedOrCancelled || isLockedConfirmedLine(product, order)
  }

  function isShipmentCreationMode(order) {
    return !!(order && order.isDataForCreateShipment)
  }

  function isCartonShipmentItem(product, order) {
    return isShipmentCreationMode(order) && product.maxShipmentQty !== undefined
  }

  function shouldValidateShipmentQty(order, isShipment, isShipmentSettingsModal) {
    return !!(order
      && (isShipmentSettingsModal
        || (!isShipment && order.status !== ORDER_STATUS.CONFIRMED && order.status !== ORDER_STATUS.REJECTED)))
  }

  function isCartonExceedingShipmentQty(product, order, isShipment, isShipmentSettingsModal) {
    return shouldValidateShipmentQty(order, isShipment, isShipmentSettingsModal)
      && isCartonShipmentItem(product, order)
      && product.quantity > product.maxShipmentQty
  }

  function getCartonMaxQty(product, order) {
    return isCartonShipmentItem(product, order) ? product.maxShipmentQty : getMaxQuantity(product.quantityAvailable)
  }

  function getCartonDisplayMaxQty(product, isShipmentSettingsModal) {
    if (isShipmentSettingsModal && product.maxShipmentQty != null) {
      return product.maxShipmentQty
    }
    return product.quantityAvailable != null ? product.quantityAvailable : ''
  }

  function shouldShowCartonMaxQty(product, isShipmentSettingsModal) {
    if (isShipmentSettingsModal) {
      return product.isCarton && (product.maxShipmentQty !== undefined || hasProductMaxQuantity(product.quantityAvailable))
    }
    return false
  }

  function isCartonInputDisabled(product, isEditEnabled, order) {
    return product.status === ORDER_PRODUCT_SHIPMENT_STATUS.CANCELLED.LABEL
      || isLockedConfirmedLine(product, order)
      || product.isEdited
      || (isEditEnabled && !isShipmentCreationMode(product))
      || isProductValueDisabled(product.quantityAvailable)
  }

  function getProductVariantItems(product, isShipment) {
    return !isShipment ? product.items : product.itemsForShipment || product.items
  }

  function isModifyEnabledForBrand(isEditEnabled, order) {
    return !isEditEnabled || isShipmentCreationMode(order)
  }

  function isRemoveEnabled(product, order, isBrand, isRetailer) {
    return isOrderLineRemovable(product, order, { isBrand, isRetailer })
  }

  function isShowStatusTag(productStatus) {
    const orderProductStatuses = Object.values(ORDER_PRODUCT_SHIPMENT_STATUS).map(
      _ => _.LABEL,
    )
    const shipmentStatuses = Object.values(SHIPMENT_STATUS)
    return orderProductStatuses.includes(productStatus) || shipmentStatuses.includes(productStatus)
  }

  // Discount helpers
  function isProductDiscountDisabled(product) {
    if (product.status === ORDER_PRODUCT_SHIPMENT_STATUS.CANCELLED.LABEL) {
      return true
    }
    const shipmentStatuses = Object.values(SHIPMENT_STATUS)
    if (shipmentStatuses.includes(product.status)) {
      return true
    }
    return false
  }

  function hasProductDiscount(product) {
    return product.discount !== undefined && product.discount !== null && product.discount < 1
  }

  function isEditingDiscount(product) {
    return !!editingDiscountProductIds.value[product.productId]
  }

  function startEditDiscount(product) {
    Vue.set(originalDiscounts.value, product.productId, productDiscounts.value[product.productId] || '')
    Vue.set(editingDiscountProductIds.value, product.productId, true)
  }

  function cancelEditDiscount(product) {
    const originalValue = originalDiscounts.value[product.productId]
    Vue.set(productDiscounts.value, product.productId, originalValue !== undefined ? originalValue : '')
    Vue.set(editingDiscountProductIds.value, product.productId, false)
  }

  async function applyProductDiscount(product, orderId, emit) {
    const discountPercent = parseFloat(productDiscounts.value[product.productId])
    const isValidDiscount = !Number.isNaN(discountPercent) && discountPercent >= 0 && discountPercent <= 100
    if (!isValidDiscount) {
      apiToastWarning('Please enter a valid discount percentage (0-100)')
      return
    }
    const discount = (100 - discountPercent) / 100

    isApplyingDiscount.value = true
    const payload = {
      products: [{ productId: product.productId, discount }],
    }

    try {
      const res = await store.dispatch(UPDATE_ORDER_PRODUCTS_DISCOUNT, {
        orderId,
        payload,
      })
      apiToastSuccess(res.data.message || 'Discount applied successfully!')
      Vue.set(editingDiscountProductIds.value, product.productId, false)
      if (emit) emit('reload-order')
    } catch (error) {
      apiToastErrorV2(error)
    } finally {
      isApplyingDiscount.value = false
    }
  }

  function initProductDiscounts(orderProducts) {
    if (!orderProducts) return
    orderProducts.forEach(product => {
      const hasDiscount = product.discount !== undefined && product.discount !== null && product.discount < 1
      if (hasDiscount) {
        const discountPercent = convertValueToPercent(product.discount)
        Vue.set(productDiscounts.value, product.productId, discountPercent)
      }
    })
  }

  // Partial shipment helpers
  function togglePartialBreakdown(productId) {
    Vue.set(
      expandedPartialProducts.value,
      productId,
      !expandedPartialProducts.value[productId],
    )
  }

  function isPartiallyShipped(product) {
    return product.status === ORDER_PRODUCT_SHIPMENT_STATUS.PARTIALLY_SHIPPED.LABEL
  }

  function hasShippedItems(product) {
    if (product.isCarton) {
      return (product.shippedQuantity || 0) > 0
    }
    return product.shippedItems && product.shippedItems.length > 0
  }

  function getRemainingUnits(product) {
    if (product.isCarton) {
      const originalQty = product.originalQuantity || product.quantity || 0
      return originalQty - (product.shippedQuantity || 0)
    }
    if (!product.lineTotalQuantity) return 0
    return (product.lineTotalQuantity || 0) - (product.shippedQuantity || 0)
  }

  function getShippedItemsForDisplay(product) {
    if (!product.items || !product.shippedItems) return []
    const shippedMap = {}
    product.shippedItems.forEach(shipped => {
      shippedMap[shipped.skuId] = shipped.shippedValue || 0
    })
    return product.items.map(item => ({
      ...item,
      value: shippedMap[item.skuId || item.sku] || 0,
    }))
  }

  function getRemainingItemsForDisplay(product) {
    if (!product.items || !product.shippedItems) return []
    const shippedMap = {}
    product.shippedItems.forEach(shipped => {
      shippedMap[shipped.skuId] = shipped.shippedValue || 0
    })
    return product.items.map(item => {
      const shipped = shippedMap[item.skuId || item.sku] || 0
      const remaining = (item.value || 0) - shipped
      return {
        ...item,
        value: remaining > 0 ? remaining : 0,
      }
    })
  }

  // Variant update helpers
  function extractVariantValues(variants) {
    const currentItems = {}
    variants.forEach(variant => {
      Object.values(variant).forEach(cell => {
        if (cell && cell.skuId) {
          currentItems[cell.skuId] = cell.value
        }
      })
    })
    return currentItems
  }

  function updateItemsForShipment(product, currentItems) {
    product.itemsForShipment.forEach(item => {
      const key = item.skuId || item.sku
      if (key && Object.prototype.hasOwnProperty.call(currentItems, key)) {
        item.value = currentItems[key] || 0
      }
    })
  }

  function updateShipmentVariants(product, variants) {
    const currentItems = extractVariantValues(variants)
    updateItemsForShipment(product, currentItems)
  }

  function haveItemsChanged(prevItems, newItems) {
    if (newItems === prevItems) return false
    if (!prevItems) return true
    if (prevItems.length !== newItems.length) return true
    return newItems.some((item, idx) => item !== prevItems[idx])
  }

  function updateStandardVariantItems(product) {
    const newItems = parseProductItems(product)
    // Guard: only replace product.items when items have actually changed.
    // parseProductItems returns a new array ref via .map(), which triggers
    // the ProductVariantsMixin `items` watcher → re-emits `update-variants`
    // → calls this function again → infinite loop.
    if (haveItemsChanged(product.items, newItems)) {
      product.items = newItems
    }
  }

  function ensureNonCartonQuantity(product) {
    if (!product.isCarton) {
      product.quantity = 1
    }
  }

  function updateProductVariants(variants, product, order) {
    product.variants = variants

    const isShipmentUpdate = order?.isDataForCreateShipment && product.itemsForShipment
    if (isShipmentUpdate) {
      updateShipmentVariants(product, variants)
    } else {
      updateStandardVariantItems(product)
    }

    ensureNonCartonQuantity(product)
    updateTotalPriceAndUnits(order)
    variantLoaded.value = true
  }

  // Shipment selection
  function onSelect(isSelected, product, index) {
    if (isSelected) {
      product.originalStatus = product.status
      product.status = ORDER_PRODUCT_SHIPMENT_STATUS.SELECTED_BY_USER.LABEL
    } else {
      product.status = product.originalStatus || ORDER_PRODUCT_SHIPMENT_STATUS.READY_TO_SHIP.LABEL
    }

    // The slice can be filtered, so its index is not the index in `products`.
    const productIndex = products.value.indexOf(product)
    Vue.set(products.value, productIndex === -1 ? index : productIndex, product)
    const isSelectedAtleastOne = products.value.find(
      p => p.status === ORDER_PRODUCT_SHIPMENT_STATUS.SELECTED_BY_USER.LABEL,
    )
    isSelectedProduct.value = !!isSelectedAtleastOne
  }

  function onSelectReadyToShipProducts(isSelected, order = null) {
    for (let i = 0; i < products.value.length; i++) {
      if (isLineShipSelectable(products.value[i], order)) {
        if (isSelected) {
          products.value[i].originalStatus = products.value[i].status
          products.value[i].status = ORDER_PRODUCT_SHIPMENT_STATUS.SELECTED_BY_USER.LABEL
        } else {
          products.value[i].status = products.value[i].originalStatus || ORDER_PRODUCT_SHIPMENT_STATUS.READY_TO_SHIP.LABEL
        }
        Vue.set(products.value, i, products.value[i])
      }
    }
    isSelectedProduct.value = isSelected
  }

  function isProductSelectedForShipment(product) {
    return product.status === ORDER_PRODUCT_SHIPMENT_STATUS.SELECTED_BY_USER.LABEL
      && getStatusTextAndColorCode(product.status).isEnabled
  }

  // Build shipped quantity map
  function buildShippedQuantityMap(shippedItems) {
    const shippedMap = {}
    if (shippedItems?.length > 0) {
      shippedItems.forEach(shipped => {
        shippedMap[shipped.skuId] = shipped.shippedValue || 0
      })
    }
    return shippedMap
  }

  function setCartonShipmentQty(productCopy) {
    const originalQty = productCopy.quantity || 0
    const shippedCartons = productCopy.shippedQuantity || 0
    productCopy.maxShipmentQty = originalQty - shippedCartons
    productCopy.originalQuantity = originalQty
    productCopy.quantity = productCopy.maxShipmentQty
  }

  function buildItemsForShipment(items, shippedMap) {
    return items.map(item => {
      const itemCopy = JSON.parse(JSON.stringify(item))
      const skuId = item.skuId || item.sku
      const shippedQty = shippedMap[skuId] || 0
      itemCopy.maxShipmentQty = (item.value || 0) - shippedQty
      itemCopy.value = itemCopy.maxShipmentQty
      return itemCopy
    })
  }

  function prepareProductForShipment(product) {
    const productCopy = JSON.parse(JSON.stringify(product))
    const shippedMap = buildShippedQuantityMap(productCopy.shippedItems)

    if (productCopy.isCarton) {
      setCartonShipmentQty(productCopy)
    }

    productCopy.itemsForShipment = buildItemsForShipment(productCopy.items, shippedMap)
    return productCopy
  }

  function shipProducts(order, emit) {
    const orderData = JSON.parse(JSON.stringify(order))
    const selectedProducts = products.value
      .filter(isProductSelectedForShipment)
      .map(prepareProductForShipment)

    orderData.isDataForCreateShipment = true
    orderData.products = selectedProducts
    emit('ship-now', orderData)
  }

  function isSelectedAll(order) {
    if (isSelectedProduct.value) {
      const selectedProducts = products.value.filter(
        product => product.status === ORDER_PRODUCT_SHIPMENT_STATUS.SELECTED_BY_USER.LABEL,
      )
      return order.readyToShipProductsCount === selectedProducts.length
    }
    return false
  }

  return {
    // State
    variantLoaded,
    isLoaded,
    isSelectedProduct,
    products,
    productsSlice,
    productSearchQuery,
    productDiscounts,
    isApplyingDiscount,
    editingDiscountProductIds,
    originalDiscounts,
    expandedPartialProducts,
    headings,
    colorHeading,

    // Methods - scroll
    onScrollLoader,

    // Methods - product setup
    setOrderProducts,
    searchOrderProducts,
    updateTotalPriceAndUnits,
    onCartonQuantityChange,

    // Methods - product status
    isProductDeletedOrCancelled,
    isProductDisabled,
    isShipmentCreationMode,
    isCartonShipmentItem,
    shouldValidateShipmentQty,
    isCartonExceedingShipmentQty,
    getCartonMaxQty,
    getCartonDisplayMaxQty,
    shouldShowCartonMaxQty,
    isCartonInputDisabled,
    getProductVariantItems,
    isModifyEnabledForBrand,
    isRemoveEnabled,
    isShowStatusTag,

    // Methods - discount
    isProductDiscountDisabled,
    hasProductDiscount,
    isEditingDiscount,
    startEditDiscount,
    cancelEditDiscount,
    applyProductDiscount,
    initProductDiscounts,
    convertValueToPercent,

    // Methods - partial shipment
    togglePartialBreakdown,
    isPartiallyShipped,
    hasShippedItems,
    getRemainingUnits,
    getShippedItemsForDisplay,
    getRemainingItemsForDisplay,

    // Methods - variants
    updateProductVariants,

    // Methods - shipment selection
    onSelect,
    onSelectReadyToShipProducts,
    isSelectedAll,
    shipProducts,

    // Utilities
    parseProductImage,
    formatNumber,
    formatCurrency,
    formattedDate,
    getStatusTextAndColorCode,
    getLineStatusText,
    isLineShipSelectable,
    imageLoadError,
    isProductValueDisabled,
    hasProductMaxQuantity,
    getMaxQuantity,

    // Constants
    ORDER_PRODUCT_SHIPMENT_STATUS,
    ORDER_STATUS,
  }
}
